/**
 * Civil (timezone-free) date helpers.
 *
 * Expiry dates are calendar facts, not instants: "haltbar bis 25.08." is the
 * same day whether you look at the panel from Berlin or from a VPS running UTC.
 * We therefore store them as 'YYYY-MM-DD' strings and never let a Date object
 * with a time component near them.
 */
const DAY_MS = 86_400_000;
export function isCivilDate(v) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
    if (!m)
        return false;
    const [year, month, day] = [Number(m[1]), Number(m[2]), Number(m[3])];
    // Round-trip: Date.UTC happily rolls 2026-02-31 into March, so compare back.
    const d = new Date(Date.UTC(year, month - 1, day));
    return (d.getUTCFullYear() === year && d.getUTCMonth() === month - 1 && d.getUTCDate() === day);
}
/** Today in the given IANA zone, as a civil date. */
export function todayIn(timeZone, now = new Date()) {
    // en-CA formats as YYYY-MM-DD, which is exactly what we want.
    return new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(now);
}
function toUtcMs(d) {
    return Date.parse(d + 'T00:00:00Z');
}
function fromUtcMs(ms) {
    return new Date(ms).toISOString().slice(0, 10);
}
export function addDays(d, days) {
    return fromUtcMs(toUtcMs(d) + days * DAY_MS);
}
/** Whole days from `from` to `to`. Negative when `to` is in the past. */
export function daysBetween(from, to) {
    return Math.round((toUtcMs(to) - toUtcMs(from)) / DAY_MS);
}
/** 'DD.MM.YYYY' — the format the panel shows and the keypad accepts. */
export function formatDe(d) {
    const [y, m, day] = d.split('-');
    return `${day}.${m}.${y}`;
}
/** Parses the 8 digits the numeric keypad collects (DDMMYYYY). */
export function parseKeypadDigits(digits) {
    if (!/^\d{8}$/.test(digits))
        return null;
    const day = digits.slice(0, 2);
    const month = digits.slice(2, 4);
    const year = digits.slice(4, 8);
    const candidate = `${year}-${month}-${day}`;
    return isCivilDate(candidate) ? candidate : null;
}
/** Monday of the week containing `d`. Weeks are Mon–Sun here, as in Germany. */
export function startOfWeek(d) {
    const dow = new Date(d + 'T00:00:00Z').getUTCDay(); // 0 = Sunday
    return addDays(d, -((dow + 6) % 7));
}
/** ISO-8601 week number — the "KW 34" in the calendar header. */
export function isoWeekNumber(d) {
    const date = new Date(d + 'T00:00:00Z');
    // Shift to the Thursday of this week; ISO weeks are numbered by their Thursday.
    date.setUTCDate(date.getUTCDate() - ((date.getUTCDay() + 6) % 7) + 3);
    const firstThursday = new Date(Date.UTC(date.getUTCFullYear(), 0, 4));
    firstThursday.setUTCDate(firstThursday.getUTCDate() - ((firstThursday.getUTCDay() + 6) % 7) + 3);
    return 1 + Math.round((date.getTime() - firstThursday.getTime()) / (7 * DAY_MS));
}
/** Local midnight of a civil date, as an ISO instant — for API range queries. */
export function localMidnightIso(d) {
    return new Date(d + 'T00:00:00').toISOString();
}
/** The civil date an instant falls on, in the viewer's local zone. */
export function civilDateOf(instant) {
    return new Date(instant).toLocaleDateString('en-CA');
}
/**
 * Offset of `timeZone` from UTC, in ms, at a given instant.
 */
function zoneOffsetMs(instantMs, timeZone) {
    const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
        timeZone,
        hour12: false,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    })
        .formatToParts(new Date(instantMs))
        .map((p) => [p.type, p.value]));
    const asUtc = Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day), 
    // Some engines render midnight as hour 24 under hour12:false.
    Number(parts.hour) % 24, Number(parts.minute), Number(parts.second));
    return asUtc - instantMs;
}
/**
 * Wall-clock time in a named zone -> ISO instant.
 *
 * Needed for ICS: calendar feeds carry floating times and all-day DATE values
 * that have no zone of their own. They mean "15:00 wherever the household is",
 * so they must be pinned to the household zone rather than to whatever the
 * server happens to be set to (a VPS is UTC).
 */
export function zonedToInstant(date, time, timeZone) {
    const [y, m, d] = date.split('-').map(Number);
    const [hh = 0, mi = 0, ss = 0] = time.split(':').map(Number);
    const naive = Date.UTC(y, m - 1, d, hh, mi, ss);
    const firstGuess = naive - zoneOffsetMs(naive, timeZone);
    // Re-check against the offset actually in force at the guessed instant; this
    // is what makes DST transitions land correctly.
    const corrected = naive - zoneOffsetMs(firstGuess, timeZone);
    return new Date(corrected).toISOString();
}
